// ─── KEN FRENCH FACTOR DATA ───────────────────────────────────────────────────
// Fetches, parses, caches, and serves Ken French factor returns. Named exports
// only, no default, no React.
//
// Primary path: the backend (/api/factors/french) fetches the Ken French ZIP,
// unzips, parses, and serves JSON. Direct fallback: fetch the public ZIP/CSV and
// parse it best-effort in the browser. French updates monthly, so we cache for
// 30 days.

import { backendFetch } from "./core";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
export const CACHE_KEY = "qs_french_factors";
export const CACHE_TIMESTAMP_KEY = "qs_french_factors_ts";
export const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const BACKEND_PATH = "/api/factors/french";

// Public Ken French 3-factor research data ZIP (used only on the direct fallback
// path). The backend is the real data source; this URL is a best-effort backup.
const FRENCH_ZIP_URL =
  "https://mba.tuck.dartmouth.edu/pages/faculty/ken.french/ftp/F-F_Research_Data_Factors_CSV.zip";

// Target output factors. Each maps to the column header aliases seen across the
// various French files (3-factor, 5-factor, momentum).
const TARGET_COLUMNS = {
  MktRF: ["Mkt-RF", "MKT-RF"],
  SMB: ["SMB"],
  HML: ["HML"],
  Mom: ["Mom", "MOM", "WML"],
  RMW: ["RMW"],
  CMA: ["CMA"],
  RF: ["RF"],
};
const FACTOR_NAMES = Object.keys(TARGET_COLUMNS);

// French missing-data sentinels.
function isMissingCode(v) {
  return !Number.isFinite(v) || v <= -99.98;
}

// ─── NORMALIZATION ────────────────────────────────────────────────────────────
// Coerce an observation coming from the backend (already-parsed JSON) into our
// canonical shape. Accepts either our field names or the raw French names.
function normalizeObservation(o) {
  if (!o || typeof o !== "object") return null;

  const rawDate = o.date ?? o.Date ?? o.month ?? o.period;
  const date = normalizeDate(rawDate);
  if (!date) return null;

  const obs = { date };
  for (const name of FACTOR_NAMES) {
    let val = o[name];
    if (val === undefined) {
      // Try the raw French header aliases (e.g. "Mkt-RF").
      for (const alias of TARGET_COLUMNS[name]) {
        if (o[alias] !== undefined) {
          val = o[alias];
          break;
        }
      }
    }
    const num = typeof val === "string" ? parseFloat(val) : val;
    obs[name] = Number.isFinite(num) ? num : null;
  }
  return obs;
}

// Accepts "YYYYMM", "YYYY-MM", "YYYY-MM-DD", or a Date/number → "YYYY-MM".
function normalizeDate(raw) {
  if (raw == null) return null;
  const str = String(raw).trim();
  if (/^\d{6}$/.test(str)) return `${str.slice(0, 4)}-${str.slice(4, 6)}`;
  if (/^\d{4}-\d{2}/.test(str)) return str.slice(0, 7);
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 7);
  return null;
}

// ─── CSV PARSING (direct fallback) ────────────────────────────────────────────
// Parse the monthly block out of a Ken French CSV. Values are percentages →
// converted to decimals. Missing factors (columns not present in this file) are
// left null. Returns [] if no header/monthly rows are found (e.g. binary ZIP).
function parseFrenchCsv(text) {
  if (typeof text !== "string" || !text.length) return [];
  const lines = text.split(/\r?\n/);

  // Find the header row (the one that names the factor columns).
  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/Mkt-?RF/i.test(lines[i])) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) return [];

  const headerCells = lines[headerIdx].split(",").map((c) => c.trim());
  // Map each target factor to its column index (data cells are offset by 1
  // because the first column is the date and is unlabeled in the header).
  const colIndex = {};
  for (const name of FACTOR_NAMES) {
    colIndex[name] = -1;
    for (let c = 0; c < headerCells.length; c++) {
      if (TARGET_COLUMNS[name].some((alias) => headerCells[c].toUpperCase() === alias.toUpperCase())) {
        colIndex[name] = c + 1; // +1: date occupies index 0 in data rows
        break;
      }
    }
  }

  const out = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const cells = lines[i].split(",").map((c) => c.trim());
    if (!/^\d{6}$/.test(cells[0])) continue; // monthly rows only (YYYYMM)
    const date = `${cells[0].slice(0, 4)}-${cells[0].slice(4, 6)}`;
    const obs = { date };
    for (const name of FACTOR_NAMES) {
      const idx = colIndex[name];
      if (idx === -1 || idx >= cells.length) {
        obs[name] = null;
        continue;
      }
      const num = parseFloat(cells[idx]);
      obs[name] = isMissingCode(num) ? null : num / 100; // % → decimal
    }
    out.push(obs);
  }
  return out;
}

// ─── fetchFrenchFactors ───────────────────────────────────────────────────────
// Primary: backend-served JSON. Fallback (no backend): direct ZIP/CSV parse.
// On any error: log and return []. Never throws.
export async function fetchFrenchFactors() {
  try {
    const res = await backendFetch(FRENCH_ZIP_URL, BACKEND_PATH);
    if (!res.ok) throw new Error(`french factors HTTP ${res.status}`);

    const raw = await res.text();

    // Backend returns parsed JSON; the direct fallback returns CSV (or a ZIP we
    // cannot unzip in-browser, in which case parseFrenchCsv yields []).
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }

    if (parsed !== null) {
      const rows = Array.isArray(parsed)
        ? parsed
        : parsed.data || parsed.observations || parsed.factors || [];
      return rows.map(normalizeObservation).filter(Boolean);
    }

    return parseFrenchCsv(raw);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("fetchFrenchFactors failed:", err);
    return [];
  }
}

// ─── CACHE ────────────────────────────────────────────────────────────────────
export function getCachedFactors() {
  try {
    const data = localStorage.getItem(CACHE_KEY);
    const ts = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!data || !ts) return null;
    if (Date.now() - Number(ts) >= CACHE_TTL_MS) return null; // expired
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setCachedFactors(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable or over quota — cache is best-effort.
  }
}

// ─── getFactors ───────────────────────────────────────────────────────────────
// Main entry point. Cache first; on miss, fetch and cache. A failed fetch ([])
// is not cached, so the next call retries rather than being stuck empty for 30d.
export async function getFactors() {
  const cached = getCachedFactors();
  if (cached) return cached;

  const data = await fetchFrenchFactors();
  if (data.length > 0) setCachedFactors(data);
  return data;
}

// ─── getFactorReturns ─────────────────────────────────────────────────────────
// Filter the factor series to a [startDate, endDate] inclusive range. Dates are
// "YYYY-MM" strings; lexical comparison is valid for that format.
export async function getFactorReturns(startDate, endDate) {
  const all = await getFactors();
  return all.filter(
    (o) =>
      (!startDate || o.date >= startDate) && (!endDate || o.date <= endDate)
  );
}

// ─── getFactorPerformanceWhenSignalFired ──────────────────────────────────────
// For each occurrence of a signal, measure the factor returns over the 1/3/6/12
// months following the fire date, then average across occurrences.
//
// Output shapes cover MktRF, SMB, HML, Mom, RMW, CMA (RF excluded).
//   avgReturn  — mean of the per-month factor returns across every post-signal
//                month (up to 12) pooled over all occurrences.
//   atNMonth   — mean compounded factor return over the N months after the
//                signal, averaged across occurrences.
// Returns null if fewer than 5 valid occurrences (insufficient sample).
const PERF_FACTORS = ["MktRF", "SMB", "HML", "Mom", "RMW", "CMA"];

export async function getFactorPerformanceWhenSignalFired(signalType, signalHistory) {
  const events = (Array.isArray(signalHistory) ? signalHistory : []).filter(
    (e) => e && e.firedAt && (!e.signalType || e.signalType === signalType)
  );
  if (events.length < 5) return null; // insufficient sample

  const all = await getFactors();
  const byDate = new Map(all.map((o) => [o.date, o]));
  const sortedDates = all.map((o) => o.date).sort();

  // The N months strictly after a fire month, in chronological order.
  const monthsAfter = (fireMonth, count) => {
    let lo = 0;
    let hi = sortedDates.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedDates[mid] <= fireMonth) lo = mid + 1;
      else hi = mid;
    }
    return sortedDates.slice(lo, lo + count).map((d) => byDate.get(d));
  };

  // Compounded factor return over a set of monthly observations.
  const compound = (obsList, factor) => {
    let acc = 1;
    let used = 0;
    for (const o of obsList) {
      const r = o?.[factor];
      if (typeof r !== "number") continue;
      acc *= 1 + r;
      used++;
    }
    return used > 0 ? acc - 1 : null;
  };

  const horizons = { at1Month: 1, at3Month: 3, at6Month: 6, at12Month: 12 };
  const sums = {};
  const counts = {};
  for (const key of [...Object.keys(horizons), "avgReturn"]) {
    sums[key] = {};
    counts[key] = {};
    for (const f of PERF_FACTORS) {
      sums[key][f] = 0;
      counts[key][f] = 0;
    }
  }

  let validOccurrences = 0;
  for (const ev of events) {
    const fireMonth = normalizeDate(ev.firedAt);
    if (!fireMonth) continue;
    validOccurrences++;

    const next12 = monthsAfter(fireMonth, 12);

    for (const [key, n] of Object.entries(horizons)) {
      const window = next12.slice(0, n);
      for (const f of PERF_FACTORS) {
        const c = compound(window, f);
        if (c !== null) {
          sums[key][f] += c;
          counts[key][f]++;
        }
      }
    }

    // avgReturn: pooled mean of individual monthly returns (up to 12 months).
    for (const o of next12) {
      for (const f of PERF_FACTORS) {
        const r = o?.[f];
        if (typeof r === "number") {
          sums.avgReturn[f] += r;
          counts.avgReturn[f]++;
        }
      }
    }
  }

  if (validOccurrences < 5) return null;

  const avgOf = (key) => {
    const out = {};
    for (const f of PERF_FACTORS) {
      out[f] = counts[key][f] > 0 ? sums[key][f] / counts[key][f] : null;
    }
    return out;
  };

  return {
    occurrences: validOccurrences,
    avgReturn: avgOf("avgReturn"),
    at1Month: avgOf("at1Month"),
    at3Month: avgOf("at3Month"),
    at6Month: avgOf("at6Month"),
    at12Month: avgOf("at12Month"),
  };
}
